import React, { type MouseEvent, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useLoyaltyProfile, useUserResources } from 'shared/hooks'

import { menuList, type SidebarSubMenu } from '../Sidebar/constants'
import * as S from './styled'

type Props = {
  mdmClientNumber?: number | string
  isLysUser?: boolean
  programType?: number | null
  userOccupation?: string
}

type WindowWithEnv = Window & {
  env?: {
    baseHost?: string
  }
}

const normalizePath = (pathname: string) => {
  if (!pathname) {
    return ''
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

const isItemActive = (currentPath: string, link: string) => {
  const cur = normalizePath(currentPath)
  const lnk = normalizePath(link)

  if (!lnk) {
    return false
  }

  if (lnk === '/fidelidade') {
    return cur === '/fidelidade'
  }

  if (cur === lnk) {
    return true
  }

  return cur.startsWith(`${lnk}/`)
}

const getBaseHost = () => {
  const customWindow = window as WindowWithEnv

  return customWindow.env?.baseHost || ''
}

const LoyaltySubmenuHorizontal: React.FC<Props> = ({
  mdmClientNumber,
  isLysUser = false,
  programType = null,
  userOccupation = ''
}) => {
  const history = useHistory()

  const [activePathname, setActivePathname] = useState<string>('')

  const {
    userControls: { isLoyaltyElegible },
    isLoadingResources
  } = useUserResources()

  const {
    loyaltyProfile,
    loyaltyStatus,
    isLoading: isLoadingLoyaltyProfile
  } = useLoyaltyProfile()

  const mdmClientNumberStr = useMemo(() => {
    if (mdmClientNumber == null) {
      return null
    }

    const s = String(mdmClientNumber).trim()

    return s || null
  }, [mdmClientNumber])

  const effectiveIsLoyaltyElegible =
    Boolean(mdmClientNumberStr) || Boolean(isLoyaltyElegible)

  const baseHost = getBaseHost()
  const isUnderLoyalty = window.location.href.includes('/fidelidade')

  const onMenuClick = (event: MouseEvent, link: string) => {
    event.preventDefault()

    if (isUnderLoyalty) {
      const newLink = link.replace('/fidelidade', '')

      history.push(newLink || '/')

      return
    }

    window.location.href = `${baseHost}${link}`
  }

  useEffect(() => {
    const update = () => {
      setActivePathname(normalizePath(window.location.pathname))
    }

    update()

    const unlisten = history.listen(() => update())

    window.addEventListener('popstate', update)

    return () => {
      if (typeof unlisten === 'function') {
        unlisten()
      }

      window.removeEventListener('popstate', update)
    }
  }, [history])

  const submenu: SidebarSubMenu[] = useMemo(() => {
    if (!effectiveIsLoyaltyElegible) {
      return []
    }

    const cashbackEnabled = Boolean(loyaltyProfile?.hasArchitectCashback)

    const items = menuList({
      loyaltyStatus,
      isLoyaltyElegible: effectiveIsLoyaltyElegible,
      cashbackEnabled,
      isLysUser: Boolean(mdmClientNumberStr) && isLysUser,
      programType: mdmClientNumberStr ? programType : null,
      userOccupation: userOccupation ?? ''
    })

    return items.find(i => i.id === 'loyalty')?.submenu ?? []
  }, [
    effectiveIsLoyaltyElegible,
    loyaltyProfile,
    loyaltyStatus,
    isLysUser,
    mdmClientNumberStr,
    programType,
    userOccupation
  ])

  const shouldShowShell =
    effectiveIsLoyaltyElegible &&
    (isLoadingResources || isLoadingLoyaltyProfile)

  if (shouldShowShell) {
    return (
      <S.OnlyMobileTablet>
        <S.SkeletonRow aria-hidden="true">
          <S.SkeletonPill />
          <S.SkeletonPill />
          <S.SkeletonPill />
        </S.SkeletonRow>
      </S.OnlyMobileTablet>
    )
  }

  if (!submenu.length) {
    return null
  }

  return (
    <S.OnlyMobileTablet>
      <S.Container aria-label="Submenu Fidelidade">
        <S.Row>
          {submenu.map(item => {
            const active = isItemActive(activePathname, item.link)

            return (
              <S.Item key={item.id} data-active={active ? 'true' : 'false'}>
                <S.ItemLink
                  href={`${baseHost}${item.link}`}
                  onClick={(event: MouseEvent) => onMenuClick(event, item.link)}
                  aria-current={active ? 'page' : undefined}
                  data-tracking={`loyalty-horizontal-subitem-${item.id}`}>
                  {item.name}
                </S.ItemLink>
              </S.Item>
            )
          })}
        </S.Row>
      </S.Container>
    </S.OnlyMobileTablet>
  )
}

export default LoyaltySubmenuHorizontal
